import {
  ChangeEventHandler,
  CSSProperties,
  forwardRef,
  useCallback,
  useId,
} from 'react';

import { colors } from '../../../styles/constants';
import {
  FranceFlagIcon,
  PHONE_COUNTRY_CONFIG,
  PhoneCountry,
  toE164PhoneNumber,
  toLocalPhoneNumber,
  UkFlagIcon,
  validatePhoneNumber,
} from './phoneCountryConfig';
import {
  UKPhoneInputContainer,
  UKPhoneInputElement,
  UKPhoneInputPrefix,
  UKPhoneInputFlagIcon,
} from './UKPhoneInput.styles';

interface UKPhoneInputProps {
  name: string;
  placeholder?: string;
  value: string;
  country: PhoneCountry;
  onChange: (fullValue: string) => void;
  onBlur?: () => void;
  style?: CSSProperties;
}

export const UKPhoneInput = forwardRef<HTMLInputElement, UKPhoneInputProps>(
  ({ name, placeholder, value, country, onChange, onBlur, style }, ref) => {
    const clipId = useId().replace(/:/g, '');
    const clipRectId = `${clipId}-rect`;
    const clipDiagId = `${clipId}-diag`;
    const { callingCode } = PHONE_COUNTRY_CONFIG[country];

    const displayValue = toLocalPhoneNumber(value, country);

    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        const raw = e.target.value.replace(/[^\d\s]/g, '');
        onChange(raw ? toE164PhoneNumber(raw, country) : '');
      },
      [country, onChange]
    );

    // Inline backgrounds: styled-components rules can lose to global/third-party CSS on some hosts;
    // inline colors still apply reliably (esp. WebKit + -webkit-appearance: none on the inner input).
    const containerStyle: CSSProperties = {
      ...style,
      backgroundColor: colors.white,
    };

    return (
      <UKPhoneInputContainer style={containerStyle}>
        <UKPhoneInputPrefix>
          <UKPhoneInputFlagIcon aria-hidden>
            {country === 'FR' ? (
              <FranceFlagIcon />
            ) : (
              <UkFlagIcon clipRectId={clipRectId} clipDiagId={clipDiagId} />
            )}
          </UKPhoneInputFlagIcon>
          <span>{callingCode}</span>
        </UKPhoneInputPrefix>
        <UKPhoneInputElement
          ref={ref}
          name={name}
          type="tel"
          autoComplete="tel-national"
          inputMode="tel"
          placeholder={placeholder || 'Phone number'}
          value={displayValue}
          onChange={handleChange}
          onBlur={onBlur}
          style={{ backgroundColor: colors.white }}
        />
      </UKPhoneInputContainer>
    );
  }
);

UKPhoneInput.displayName = 'UKPhoneInput';

/** @deprecated Use validatePhoneNumber(value, country) */
export function validateUKPhoneNumber(value: string): string | true {
  return validatePhoneNumber(value, 'GB');
}

export { validatePhoneNumber };
